import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaBolt,
  FaBriefcase,
  FaBuilding,
  FaCheckCircle,
  FaCrown,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
  FaMapMarkerAlt,
  FaMapPin,
  FaPhone,
  FaShieldAlt,
  FaTruck,
  FaUser,
  FaUserCog,
  FaUserFriends,
  FaUserPlus,
} from "react-icons/fa";
import { FaChevronDown } from "react-icons/fa6";
import AuthService from "../services/AuthService";
import AuthPageLogo from "../components/AuthPageLogo";
import { buildLoginPath } from "../utils/authRedirect";
import useAuthFounderHeroImage from "../hooks/useAuthFounderHeroImage";
import usePlatformLogo from "../hooks/usePlatformLogo";
import "../assets/styles/signup-page.scss";

const SIGNUP_HERO_FEATURES = [
  {
    icon: FaShieldAlt,
    title: "Secure & Trusted",
    copy: "Your data is always protected.",
  },
  {
    icon: FaCheckCircle,
    title: "Verified Platform",
    copy: "Trusted by thousands of businesses.",
  },
  {
    icon: FaBolt,
    title: "Quick Onboarding",
    copy: "Create your account in minutes.",
  },
  {
    icon: FaUserFriends,
    title: "Business Matching",
    copy: "Connect with verified buyers & sellers.",
  },
  {
    icon: FaTruck,
    title: "Logistics Network",
    copy: "Access end-to-end logistics services.",
  },
];

function SignupField({
  id,
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  icon: Icon,
  required = true,
  showToggle = false,
  showPassword = false,
  onTogglePassword,
}) {
  const inputType = showToggle ? (showPassword ? "text" : "password") : type;

  return (
    <div className="signup-page__field">
      <label className="signup-page__label" htmlFor={id}>
        {label}
      </label>
      <div className="signup-page__input-wrap">
        <Icon className="signup-page__input-icon" aria-hidden />
        <input
          id={id}
          className="signup-page__input"
          name={name}
          type={inputType}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          required={required}
          autoComplete={name}
        />
        {showToggle ? (
          <button
            type="button"
            className="signup-page__input-toggle"
            onClick={onTogglePassword}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SignupSelect({ id, label, name, value, onChange, icon: Icon, children }) {
  return (
    <div className="signup-page__field">
      <label className="signup-page__label" htmlFor={id}>
        {label}
      </label>
      <div className="signup-page__input-wrap">
        <Icon className="signup-page__input-icon" aria-hidden />
        <select
          id={id}
          className="signup-page__select"
          name={name}
          value={value}
          onChange={onChange}
        >
          {children}
        </select>
        <FaChevronDown className="signup-page__select-chevron" aria-hidden />
      </div>
    </div>
  );
}

const TRADE_ROLE_OPTIONS = new Set(["Buyer", "Seller", "Both"]);

export default function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const heroImageUrl = useAuthFounderHeroImage();
  const platformLogoUrl = usePlatformLogo();

  const [form, setForm] = useState({
    name: "",
    company_name: "",
    address: "",
    pincode: "",
    tradeRole: "",
    servicesRole: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    share_contact_info: false,
    share_documents: false,
    accepts_terms: false,
  });

  useEffect(() => {
    const tradeRole = String(searchParams.get("tradeRole") || "").trim();
    if (TRADE_ROLE_OPTIONS.has(tradeRole)) {
      setForm((prev) => (prev.tradeRole ? prev : { ...prev, tradeRole }));
    }
    const servicesRole = String(searchParams.get("servicesRole") || "").trim();
    if (servicesRole) {
      setForm((prev) => (prev.servicesRole ? prev : { ...prev, servicesRole }));
    }
  }, [searchParams]);

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const otpRefs = useRef([]);

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name";
    if (!form.company_name.trim()) return "Please enter your company name";
    if (!form.address.trim()) return "Please enter your address";
    if (!/^\d{5,6}$/.test(form.pincode)) return "Enter a valid pincode";
    if (!form.tradeRole && !form.servicesRole) {
      return "Please select at least one role (Trade Role or Services Role)";
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Enter a valid email";
    if (!/^\d{7,15}$/.test(form.phone.replace(/\D/g, ""))) return "Enter a valid phone number";
    if (form.password.length < 8) return "Password must be at least 8 characters";
    if (form.password !== form.confirmPassword) return "Passwords do not match";
    if (!form.accepts_terms) return "You must accept the Terms & Conditions";
    return null;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        password: form.password,
        phone: form.phone,
        company_name: form.company_name,
        address: form.address,
        pincode: form.pincode,
        tradeRole: form.tradeRole,
        servicesRole: form.servicesRole || null,
        share_contact_info: form.share_contact_info,
        share_documents: form.share_documents,
        accepts_terms: form.accepts_terms,
      };

      const { status, data } = await AuthService.register(payload);

      if (status === 201 && data?.success) {
        toast.success(data?.message || "User registered successfully. Please verify your OTP.");
        setShowOtp(true);
        setTimeout(() => otpRefs.current[0]?.focus(), 0);
      } else {
        toast.error(data?.message || "Sign up failed.");
      }
    } catch (error) {
      let msg = "Sign up failed. Please try again.";

      if (
        error?.code === "ECONNREFUSED" ||
        error?.code === "ERR_NETWORK" ||
        error?.message?.includes("Network Error") ||
        error?.message?.includes("Failed to fetch")
      ) {
        msg = "Cannot connect to server. Please make sure the backend server is running.";
      } else if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
        msg = "Request timed out. Please check your connection and try again.";
      } else if (error?.response?.data) {
        if (error.response.data.errors && Array.isArray(error.response.data.errors)) {
          msg = error.response.data.errors.join(", ");
        } else if (error.response.data.message) {
          msg = error.response.data.message;
        } else if (error.response.data.error) {
          msg = error.response.data.error;
        }
      } else if (error?.message) {
        msg = error.message;
      }

      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const setOtpValue = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const next = [...otp];
        next[index] = "";
        setOtp(next);
      } else if (index > 0) {
        otpRefs.current[index - 1]?.focus();
        const next = [...otp];
        next[index - 1] = "";
        setOtp(next);
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      otpRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!paste) return;
    e.preventDefault();
    const digits = paste.slice(0, 6).split("");
    const next = Array(6).fill("");
    for (let i = 0; i < digits.length; i += 1) next[i] = digits[i];
    setOtp(next);
    const lastIndex = Math.min(digits.length, 6) - 1;
    otpRefs.current[lastIndex]?.focus();
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length !== 6) return toast.error("Enter the 6-digit OTP.");

    setLoading(true);
    try {
      const { status, data } = await AuthService.verifyOtp(form.email, code);
      if ((status === 200 || status === 201) && data?.success) {
        toast.success(data?.message || "OTP verified successfully.");
        const invitePopup = searchParams.get("invitePopup") === "1";
        if (invitePopup) {
          navigate(buildLoginPath("/logistics-profile/edit?invitePopup=1"));
        } else {
          navigate("/login");
        }
      } else {
        toast.error(data?.message || "OTP verification failed.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-page__shell">
        <aside className="signup-page__hero" aria-label="Founder access overview">
          <div
            className="signup-page__hero-bg"
            style={{ backgroundImage: `url(${heroImageUrl})` }}
            aria-hidden
          />
          <div className="signup-page__hero-overlay" aria-hidden />

          <div className="signup-page__hero-body">
            <div className="signup-page__hero-main">
              <Link to="/home" className="signup-page__brand" aria-label="Trocanet home">
                <img
                  src={platformLogoUrl}
                  alt="Trocanet"
                  className="signup-page__brand-logo"
                />
              </Link>

              <h1 className="signup-page__hero-title">
                Founder access.
                <span>Early opportunities for trade.</span>
              </h1>
              <p className="signup-page__hero-copy">
                Be among the first to unlock high-value opportunities and grow with a trusted trade
                network.
              </p>

              <ul className="signup-page__features">
                {SIGNUP_HERO_FEATURES.map((feature) => {
                  const Icon = feature.icon;
                  return (
                    <li key={feature.title} className="signup-page__feature">
                      <span className="signup-page__feature-icon" aria-hidden>
                        <Icon />
                      </span>
                      <div className="signup-page__feature-text">
                        <p className="signup-page__feature-title">{feature.title}</p>
                        <p className="signup-page__feature-copy">{feature.copy}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="signup-page__hero-banner">
              <span className="signup-page__hero-banner-icon" aria-hidden>
                <FaCrown />
              </span>
              <span className="signup-page__hero-banner-text">
                <strong>Founder access</strong>
                <span className="signup-page__hero-banner-sep" aria-hidden />
                <span className="signup-page__hero-banner-tagline">Build. Connect. Grow together.</span>
              </span>
            </div>
          </div>
        </aside>

        <main className="signup-page__form-panel">
          <div className="signup-page__form-inner">
            <div className="signup-page__logo">
              <AuthPageLogo />
            </div>

            <div className="signup-page__heading">
              <h2 className="signup-page__title">Sign Up</h2>
              <p className="signup-page__subtitle">Create your business account</p>
            </div>

            {!showOtp ? (
              <form onSubmit={handleRegister} noValidate>
                <div className="signup-page__grid">
                  <SignupField
                    id="name"
                    label="Your Name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="John Doe"
                    icon={FaUser}
                  />
                  <SignupField
                    id="company_name"
                    label="Company Name"
                    name="company_name"
                    value={form.company_name}
                    onChange={onChange}
                    placeholder="Doe Inc."
                    icon={FaBuilding}
                  />
                  <SignupField
                    id="address"
                    label="Address"
                    name="address"
                    value={form.address}
                    onChange={onChange}
                    placeholder="123 Main St"
                    icon={FaMapMarkerAlt}
                  />
                  <SignupField
                    id="pincode"
                    label="Pincode"
                    name="pincode"
                    value={form.pincode}
                    onChange={onChange}
                    placeholder="123456"
                    icon={FaMapPin}
                  />
                  <SignupSelect
                    id="tradeRole"
                    label="Trade Role"
                    name="tradeRole"
                    value={form.tradeRole}
                    onChange={onChange}
                    icon={FaBriefcase}
                  >
                    <option value="">Select Trade Role</option>
                    <option value="Buyer">Buyer</option>
                    <option value="Seller">Seller</option>
                    <option value="Both">Both — act as buyer and seller</option>
                  </SignupSelect>
                  <SignupSelect
                    id="servicesRole"
                    label="Services Role"
                    name="servicesRole"
                    value={form.servicesRole}
                    onChange={onChange}
                    icon={FaUserCog}
                  >
                    <option value="">Select Services Role</option>
                    <option value="Quality Inspector">Quality Inspector</option>
                    <option value="Legal Advisor">Legal Advisor</option>
                    <option value="Consultant">Consultant</option>
                    <option value="Logistics">Logistics</option>
                    <option value="Contract Manufacturer">Contract Manufacturer</option>
                    <option value="Infrastructure Leasing">Infrastructure Leasing</option>
                    <option value="Bank">Bank</option>
                    <option value="Insurance">Insurance</option>
                    <option value="Franchise">Franchise</option>
                  </SignupSelect>
                  <SignupField
                    id="phone"
                    label="Phone Number"
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="9876543210"
                    icon={FaPhone}
                  />
                  <SignupField
                    id="email"
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="name@example.com"
                    icon={FaEnvelope}
                  />
                  <SignupField
                    id="password"
                    label="Password"
                    name="password"
                    value={form.password}
                    onChange={onChange}
                    placeholder="••••••••"
                    icon={FaLock}
                    showToggle
                    showPassword={showPassword}
                    onTogglePassword={() => setShowPassword((open) => !open)}
                  />
                  <SignupField
                    id="confirmPassword"
                    label="Confirm Password"
                    name="confirmPassword"
                    value={form.confirmPassword}
                    onChange={onChange}
                    placeholder="••••••••"
                    icon={FaLock}
                    showToggle
                    showPassword={showConfirmPassword}
                    onTogglePassword={() => setShowConfirmPassword((open) => !open)}
                  />

                  <div className="signup-page__field signup-page__field--full">
                    <div className="signup-page__checks">
                      <label className="signup-page__check" htmlFor="share_contact_info">
                        <input
                          id="share_contact_info"
                          type="checkbox"
                          name="share_contact_info"
                          checked={form.share_contact_info}
                          onChange={onChange}
                        />
                        Share my contact information with relevant Buyer/Seller
                      </label>
                      <label className="signup-page__check" htmlFor="share_documents">
                        <input
                          id="share_documents"
                          type="checkbox"
                          name="share_documents"
                          checked={form.share_documents}
                          onChange={onChange}
                        />
                        Share documents securely via the portal
                      </label>
                      <label className="signup-page__check" htmlFor="accepts_terms">
                        <input
                          id="accepts_terms"
                          type="checkbox"
                          name="accepts_terms"
                          checked={form.accepts_terms}
                          onChange={onChange}
                          required
                        />
                        <span>
                          I agree to the <Link to="/contact">Terms &amp; Conditions</Link>
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="signup-page__field signup-page__field--full">
                    <button type="submit" className="signup-page__submit" disabled={loading}>
                      <FaUserPlus aria-hidden />
                      {loading ? "Please wait..." : "Create Account"}
                    </button>
                  </div>
                </div>
              </form>
            ) : (
              <>
                <p className="signup-page__otp-copy">
                  We&apos;ve sent a 6-digit OTP to <strong>{form.email}</strong>. It&apos;s valid for 10
                  minutes.
                </p>
                <form onSubmit={handleVerifyOtp}>
                  <div className="signup-page__otp-row" onPaste={handleOtpPaste}>
                    {otp.map((val, idx) => (
                      <input
                        key={idx}
                        ref={(el) => {
                          otpRefs.current[idx] = el;
                        }}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        pattern="\d{1}"
                        maxLength={1}
                        className="signup-page__otp-input"
                        value={val}
                        onChange={(e) => setOtpValue(idx, e.target.value.replace(/\D/g, ""))}
                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      />
                    ))}
                  </div>
                  <button type="submit" className="signup-page__submit" disabled={loading}>
                    {loading ? "Verifying..." : "Verify OTP"}
                  </button>
                </form>
                <button
                  type="button"
                  className="signup-page__otp-change"
                  onClick={() => {
                    setShowOtp(false);
                    setOtp(Array(6).fill(""));
                  }}
                >
                  Change email
                </button>
              </>
            )}

            <p className="signup-page__login-link">
              Already have an account? <Link to="/login">Log in</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
